import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Plus, Upload, Trash2, Edit, Save, X, Loader2, Package, Palette, DollarSign, Scissors } from 'lucide-react';
import AppHeader from '../components/AppHeader';

const InternalCatalog = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Add style modal
  const [showAdd, setShowAdd] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newStyle, setNewStyle] = useState({ style_code: '', brand: '', style_name: '', description: '' });

  // Edit style modal
  const [editTarget, setEditTarget] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  // Add color modal (per-item) — supports 4 views per color
  const [colorTarget, setColorTarget] = useState(null); // item being edited
  const [colorForm, setColorForm] = useState({
    color_name: '', hex: '#000000',
    views: { front: '', back: '', left_sleeve: '', right_sleeve: '' },
  });
  const [uploadingColor, setUploadingColor] = useState(false);
  const fileInputRefs = {
    front: useRef(null), back: useRef(null), left_sleeve: useRef(null), right_sleeve: useRef(null),
  };

  // Pricing modal (per-item) — manages the default size→price table for the style
  const COMMON_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];
  const [pricingTarget, setPricingTarget] = useState(null);
  const [pricingRows, setPricingRows] = useState([]); // [{size, price}]
  const [savingPricing, setSavingPricing] = useState(false);

  const openPricing = (item) => {
    setPricingTarget(item);
    const existing = (item.sizes || []).map((s) => ({ size: s.size, price: Number(s.price || 0) }));
    setPricingRows(existing.length ? existing : []);
  };

  const addPricingRow = (size) => {
    if (!size) return;
    if (pricingRows.some((r) => r.size.toLowerCase() === size.toLowerCase())) {
      toast.error(`${size} already added`);
      return;
    }
    setPricingRows((r) => [...r, { size, price: 0 }]);
  };

  const updatePricingRow = (idx, field, value) => {
    setPricingRows((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: field === 'price' ? Number(value) || 0 : value } : r)));
  };

  const removePricingRow = (idx) => {
    setPricingRows((rows) => rows.filter((_, i) => i !== idx));
  };

  const savePricing = async () => {
    if (!pricingTarget) return;
    setSavingPricing(true);
    try {
      await axios.patch(`/internal-catalog/${pricingTarget.id}`, { sizes: pricingRows });
      toast.success('Pricing saved');
      setPricingTarget(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save pricing');
    } finally {
      setSavingPricing(false);
    }
  };

  const load = async () => {
    try {
      const res = await axios.get('/internal-catalog');
      setItems(res.data || []);
    } catch (e) {
      toast.error('Failed to load catalog');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createStyle = async () => {
    if (!newStyle.style_code.trim() || !newStyle.brand.trim() || !newStyle.style_name.trim()) {
      toast.error('Style code, brand, and name are required');
      return;
    }
    setAdding(true);
    try {
      await axios.post('/internal-catalog', newStyle);
      toast.success(`${newStyle.brand} ${newStyle.style_code} added`);
      setShowAdd(false);
      setNewStyle({ style_code: '', brand: '', style_name: '', description: '' });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add style');
    } finally {
      setAdding(false);
    }
  };

  const saveEdit = async () => {
    if (!editTarget) return;
    setSavingEdit(true);
    try {
      const { id, style_code, brand, style_name, description } = editTarget;
      await axios.patch(`/internal-catalog/${id}`, { style_code, brand, style_name, description });
      toast.success('Updated');
      setEditTarget(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally {
      setSavingEdit(false);
    }
  };

  const deleteStyle = async (item) => {
    if (!window.confirm(`Delete ${item.brand} ${item.style_code} and all its colors?`)) return;
    try {
      await axios.delete(`/internal-catalog/${item.id}`);
      toast.success('Deleted');
      await load();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const VIEW_KEYS = ['front', 'back', 'left_sleeve', 'right_sleeve'];
  const VIEW_LABELS = { front: 'Front', back: 'Back', left_sleeve: 'Left Sleeve', right_sleeve: 'Right Sleeve' };

  const openAddColor = (item, existingColor = null) => {
    setColorTarget(item);
    if (existingColor) {
      // Edit existing color — pre-fill with current data
      const existingViews = existingColor.views || {};
      // Legacy fallback: if no views, treat image_base64 as front
      const frontFallback = (!existingViews.front && existingColor.image_base64) ? existingColor.image_base64 : '';
      setColorForm({
        color_name: existingColor.color_name || '',
        hex: existingColor.hex || '#000000',
        views: {
          front: existingViews.front || frontFallback || '',
          back: existingViews.back || '',
          left_sleeve: existingViews.left_sleeve || '',
          right_sleeve: existingViews.right_sleeve || '',
        },
      });
    } else {
      setColorForm({
        color_name: '', hex: '#000000',
        views: { front: '', back: '', left_sleeve: '', right_sleeve: '' },
      });
    }
  };

  // Client-side image compression — downscales + re-encodes to JPEG so phone
  // photos (often 4–10 MB) don't exceed the proxy's ~1 MB JSON payload limit
  // when sent to /api/internal-catalog/{id}/colors.
  const compressImage = (file, { maxDim = 1400, quality = 0.82 } = {}) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.onload = (ev) => {
        const img = new Image();
        img.onerror = () => reject(new Error('Failed to decode image'));
        img.onload = () => {
          let { width, height } = img;
          if (width > maxDim || height > maxDim) {
            if (width >= height) {
              height = Math.round((height * maxDim) / width);
              width = maxDim;
            } else {
              width = Math.round((width * maxDim) / height);
              height = maxDim;
            }
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          // Fill white so transparent PNGs become valid JPEG (no black artifacts)
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.src = ev.target.result;
      };
      reader.readAsDataURL(file);
    });

  const handleViewFile = (viewKey) => async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) {
      toast.error('Image too large (max 20 MB)');
      return;
    }
    try {
      const compressed = await compressImage(f);
      setColorForm((c) => ({
        ...c, views: { ...c.views, [viewKey]: compressed },
      }));
    } catch (err) {
      toast.error('Could not process image');
    } finally {
      // Allow re-uploading the same file (e.g., after clearing)
      if (e.target) e.target.value = '';
    }
  };

  const clearView = (viewKey) => {
    setColorForm((c) => ({ ...c, views: { ...c.views, [viewKey]: '' } }));
  };

  // Per-view "Remove Background" — sends the current image to the backend (rembg) and
  // replaces the slot with a transparent-background version.
  const [removingBgKey, setRemovingBgKey] = useState(null); // which view is currently processing
  const removeBackgroundFor = async (viewKey) => {
    const current = colorForm.views[viewKey];
    if (!current) { toast.error('Upload a photo first'); return; }
    setRemovingBgKey(viewKey);
    try {
      const res = await axios.post('/images/remove-background-json', { image_base64: current });
      if (!res.data?.success) {
        toast.error(res.data?.error || 'Background removal failed');
        return;
      }
      setColorForm((c) => ({ ...c, views: { ...c.views, [viewKey]: res.data.image_base64 } }));
      toast.success(`Background removed (${res.data.engine || 'AI'})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Background removal failed');
    } finally {
      setRemovingBgKey(null);
    }
  };

  const saveColor = async () => {
    if (!colorTarget) return;
    if (!colorForm.color_name.trim()) { toast.error('Color name required'); return; }
    // Require at least one view photo
    const hasAny = VIEW_KEYS.some((k) => colorForm.views[k]);
    if (!hasAny) { toast.error('Upload at least one view photo'); return; }
    setUploadingColor(true);
    try {
      // Backend merges — so we only send the views the user actually provided
      const viewsPayload = {};
      VIEW_KEYS.forEach((k) => { if (colorForm.views[k]) viewsPayload[k] = colorForm.views[k]; });
      await axios.post(`/internal-catalog/${colorTarget.id}/colors`, {
        color_name: colorForm.color_name,
        hex: colorForm.hex,
        views: viewsPayload,
      });
      toast.success(`${colorForm.color_name} saved`);
      setColorTarget(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save color');
    } finally {
      setUploadingColor(false);
    }
  };

  const deleteColor = async (item, colorName) => {
    if (!window.confirm(`Remove ${colorName} from ${item.style_code}?`)) return;
    try {
      await axios.delete(`/internal-catalog/${item.id}/colors/${encodeURIComponent(colorName)}`);
      toast.success('Color removed');
      await load();
    } catch (e) {
      toast.error('Failed to remove color');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }} data-testid="internal-catalog-page">
      <AppHeader />
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '24px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 10 }}>
              <Package size={26} /> Internal Catalog
            </h1>
            <p style={{ color: '#64748b', fontSize: 14, marginTop: 4 }}>
              Your own library of blank styles + real product photos. Used FIRST by the Mockup Designer (vendor APIs are the fallback).
            </p>
          </div>
          <Button onClick={() => setShowAdd(true)} data-testid="add-style-btn"
            style={{ background: '#dc2626', color: '#fff' }}>
            <Plus size={16} style={{ marginRight: 6 }} /> Add Style
          </Button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 64, color: '#94a3b8' }}>
            <Loader2 className="animate-spin" size={32} style={{ margin: '0 auto' }} />
          </div>
        ) : items.length === 0 ? (
          <div style={{ background: '#fff', padding: 48, borderRadius: 12, border: '1px dashed #cbd5e1', textAlign: 'center' }}>
            <Package size={40} color="#94a3b8" style={{ margin: '0 auto 12px' }} />
            <h3 style={{ fontWeight: 700, color: '#334155', fontSize: 16 }}>No styles yet</h3>
            <p style={{ color: '#64748b', fontSize: 13, marginTop: 6 }}>
              Add your most-sold blank styles (G500, NL3600, BC3001…) with real product photos.
              Designers will see them as the first option in the Mockup Designer.
            </p>
            <Button onClick={() => setShowAdd(true)} style={{ marginTop: 16, background: '#dc2626', color: '#fff' }}>
              <Plus size={16} style={{ marginRight: 6 }} /> Add your first style
            </Button>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
            {items.map((item) => (
              <div key={item.id} data-testid={`catalog-item-${item.style_code}`}
                style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                <div style={{ padding: 16, borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {item.brand}
                      </div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
                        {item.style_code}
                      </div>
                      <div style={{ fontSize: 13, color: '#475569', marginTop: 2 }}>
                        {item.style_name}
                      </div>
                      {item.description && (
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, lineHeight: 1.4 }}>
                          {item.description}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <Button size="icon" variant="ghost" onClick={() => setEditTarget({ ...item })}
                        data-testid={`edit-${item.style_code}`}>
                        <Edit size={14} />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => deleteStyle(item)}
                        data-testid={`delete-${item.style_code}`}
                        style={{ color: '#dc2626' }}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                </div>

                <div style={{ padding: 12 }}>
                  {/* Pricing summary row */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, padding: '6px 8px', background: '#f8fafc', borderRadius: 6 }}>
                    <div style={{ fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <DollarSign size={12} />
                      {(item.sizes || []).length === 0 ? (
                        <span style={{ color: '#dc2626' }}>No pricing set</span>
                      ) : (
                        <span>
                          {item.sizes.length} size{item.sizes.length === 1 ? '' : 's'}
                          {' · '}
                          ${Math.min(...item.sizes.map((s) => Number(s.price) || 0)).toFixed(2)}
                          {item.sizes.length > 1 && ` – $${Math.max(...item.sizes.map((s) => Number(s.price) || 0)).toFixed(2)}`}
                        </span>
                      )}
                    </div>
                    <Button size="sm" variant="outline" onClick={() => openPricing(item)}
                      data-testid={`pricing-${item.style_code}`}>
                      <DollarSign size={12} style={{ marginRight: 4 }} /> Pricing
                    </Button>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Palette size={12} /> {(item.colors || []).length} color{(item.colors || []).length === 1 ? '' : 's'}
                    </div>
                    <Button size="sm" variant="outline" onClick={() => openAddColor(item)}
                      data-testid={`add-color-${item.style_code}`}>
                      <Plus size={12} style={{ marginRight: 4 }} /> Add Color
                    </Button>
                  </div>
                  {(item.colors || []).length === 0 ? (
                    <div style={{ fontSize: 11, color: '#94a3b8', padding: 16, textAlign: 'center', background: '#f8fafc', borderRadius: 6 }}>
                      No colors yet. Add a colorway with a real product photo.
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(82px, 1fr))', gap: 6 }}>
                      {(item.colors || []).map((c, i) => {
                        const v = c.views || {};
                        const frontImg = v.front || c.image_base64 || null;
                        const viewCount = ['front', 'back', 'left_sleeve', 'right_sleeve']
                          .filter((k) => v[k]).length || (c.image_base64 ? 1 : 0);
                        return (
                          <div key={i} style={{ position: 'relative', background: '#f8fafc', borderRadius: 6, overflow: 'hidden', cursor: 'pointer' }}
                            onClick={() => openAddColor(item, c)}
                            title="Click to edit views"
                            data-testid={`catalog-color-${item.style_code}-${c.color_name}`}>
                            {frontImg ? (
                              <img src={frontImg} alt={c.color_name}
                                style={{ width: '100%', height: 80, objectFit: 'cover', display: 'block' }} />
                            ) : (
                              <div style={{ width: '100%', height: 80, background: c.hex || '#e5e7eb' }} />
                            )}
                            {/* View-count badge */}
                            <div style={{
                              position: 'absolute', top: 2, left: 2,
                              background: viewCount >= 4 ? '#10b981' : '#7c3aed',
                              color: '#fff', fontSize: 9, fontWeight: 700,
                              padding: '2px 5px', borderRadius: 3,
                            }}>
                              {viewCount}/4
                            </div>
                            <div style={{ padding: '4px 6px', fontSize: 10, color: '#334155', textAlign: 'center', borderTop: '1px solid #e2e8f0' }}>
                              {c.color_name}
                            </div>
                            <button onClick={(e) => { e.stopPropagation(); deleteColor(item, c.color_name); }}
                              style={{
                                position: 'absolute', top: 2, right: 2,
                                background: 'rgba(220,38,38,0.9)', color: '#fff',
                                border: 'none', borderRadius: 4, width: 18, height: 18,
                                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}
                              title="Remove color">
                              <X size={11} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Style modal */}
      <Dialog open={showAdd} onOpenChange={(o) => !o && setShowAdd(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Style to Internal Catalog</DialogTitle>
            <DialogDescription>
              Create a style entry. You'll add color photos after.
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '12px 0' }}>
            <div>
              <Label>Style code *</Label>
              <Input placeholder="e.g. NL3600, G500, BC3001" value={newStyle.style_code}
                onChange={(e) => setNewStyle((s) => ({ ...s, style_code: e.target.value }))}
                data-testid="new-style-code" />
            </div>
            <div>
              <Label>Brand *</Label>
              <Input placeholder="e.g. Next Level, Gildan, Bella+Canvas" value={newStyle.brand}
                onChange={(e) => setNewStyle((s) => ({ ...s, brand: e.target.value }))}
                data-testid="new-style-brand" />
            </div>
            <div>
              <Label>Style name *</Label>
              <Input placeholder="e.g. Premium Fitted Crew" value={newStyle.style_name}
                onChange={(e) => setNewStyle((s) => ({ ...s, style_name: e.target.value }))}
                data-testid="new-style-name" />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea rows={2} placeholder="Fabric, weight, fit notes…" value={newStyle.description}
                onChange={(e) => setNewStyle((s) => ({ ...s, description: e.target.value }))}
                data-testid="new-style-description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)} disabled={adding}>Cancel</Button>
            <Button onClick={createStyle} disabled={adding} style={{ background: '#dc2626', color: '#fff' }}
              data-testid="save-style-btn">
              {adding ? <><Loader2 className="animate-spin" size={14} style={{ marginRight: 6 }} />Saving…</> : <>Save</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Style modal */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Style</DialogTitle>
            <DialogDescription>Update the style metadata. Color photos are managed separately.</DialogDescription>
          </DialogHeader>
          {editTarget && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '12px 0' }}>
              <div>
                <Label>Style code</Label>
                <Input value={editTarget.style_code} data-testid="edit-style-code"
                  onChange={(e) => setEditTarget((s) => ({ ...s, style_code: e.target.value }))} />
              </div>
              <div>
                <Label>Brand</Label>
                <Input value={editTarget.brand} data-testid="edit-style-brand"
                  onChange={(e) => setEditTarget((s) => ({ ...s, brand: e.target.value }))} />
              </div>
              <div>
                <Label>Style name</Label>
                <Input value={editTarget.style_name} data-testid="edit-style-name"
                  onChange={(e) => setEditTarget((s) => ({ ...s, style_name: e.target.value }))} />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea rows={2} value={editTarget.description || ''} data-testid="edit-style-description"
                  onChange={(e) => setEditTarget((s) => ({ ...s, description: e.target.value }))} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)} disabled={savingEdit}>Cancel</Button>
            <Button onClick={saveEdit} disabled={savingEdit} style={{ background: '#dc2626', color: '#fff' }}
              data-testid="edit-save-style-btn">
              {savingEdit ? <><Loader2 className="animate-spin" size={14} style={{ marginRight: 6 }} />Saving…</> : <><Save size={14} style={{ marginRight: 6 }} />Save</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pricing modal */}
      <Dialog open={!!pricingTarget} onOpenChange={(o) => !o && setPricingTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pricing · {pricingTarget?.brand} {pricingTarget?.style_code}</DialogTitle>
            <DialogDescription>
              Set the price per size. This is the default applied to every color (you can override per-color later if needed).
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '12px 0' }}>
            {pricingRows.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', background: '#f8fafc', borderRadius: 8, color: '#64748b', fontSize: 13 }}>
                No sizes yet. Add common sizes below or type a custom size.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {pricingRows.map((row, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '90px 1fr 32px', gap: 8, alignItems: 'center' }}>
                    <Input value={row.size} onChange={(e) => updatePricingRow(i, 'size', e.target.value)}
                      style={{ fontWeight: 600 }} data-testid={`pricing-size-${i}`} />
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>$</span>
                      <Input type="number" step="0.01" min="0" value={row.price}
                        onChange={(e) => updatePricingRow(i, 'price', e.target.value)}
                        style={{ paddingLeft: 22 }} data-testid={`pricing-price-${i}`} />
                    </div>
                    <Button size="icon" variant="ghost" onClick={() => removePricingRow(i)} style={{ color: '#dc2626' }}>
                      <X size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div>
              <Label className="text-xs">Quick add sizes</Label>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                {COMMON_SIZES.map((s) => {
                  const added = pricingRows.some((r) => r.size.toLowerCase() === s.toLowerCase());
                  return (
                    <Button key={s} size="sm" variant={added ? 'ghost' : 'outline'}
                      disabled={added} onClick={() => addPricingRow(s)}
                      style={{ minWidth: 44 }}>
                      {s}
                    </Button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPricingTarget(null)} disabled={savingPricing}>Cancel</Button>
            <Button onClick={savePricing} disabled={savingPricing} style={{ background: '#dc2626', color: '#fff' }}
              data-testid="save-pricing-btn">
              {savingPricing ? <><Loader2 className="animate-spin" size={14} style={{ marginRight: 6 }} />Saving…</> : <>Save Pricing</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Color / Views modal */}
      <Dialog open={!!colorTarget} onOpenChange={(o) => !o && setColorTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage Color · {colorTarget?.brand} {colorTarget?.style_code}</DialogTitle>
            <DialogDescription>
              Upload up to 4 product photos per color (Front / Back / Left Sleeve / Right Sleeve). Mockup Designer auto-loads each view onto the matching canvas side.
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '12px 0' }}>
            <div>
              <Label>Color name *</Label>
              <Input placeholder="e.g. Heather Gray, Midnight Navy" value={colorForm.color_name}
                onChange={(e) => setColorForm((c) => ({ ...c, color_name: e.target.value }))}
                data-testid="color-name-input" />
            </div>
            <div>
              <Label>Hex (optional)</Label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="color" value={colorForm.hex || '#000000'}
                  onChange={(e) => setColorForm((c) => ({ ...c, hex: e.target.value }))}
                  style={{ width: 40, height: 40, padding: 0, border: '1px solid #cbd5e1', borderRadius: 6 }} />
                <Input value={colorForm.hex || ''} placeholder="#000000"
                  onChange={(e) => setColorForm((c) => ({ ...c, hex: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Product photos · 4 views</Label>
              <p style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                Upload Front, Back, Left Sleeve, Right Sleeve. Mockup Designer will load each view on the matching canvas side. Only Front is required.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginTop: 8 }}>
                {VIEW_KEYS.map((vk) => {
                  const img = colorForm.views[vk];
                  return (
                    <div key={vk} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          {VIEW_LABELS[vk]} {vk === 'front' && <span style={{ color: '#dc2626' }}>*</span>}
                        </span>
                        {img && (
                          <button onClick={() => clearView(vk)}
                            style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: 0, fontSize: 10 }}>
                            clear
                          </button>
                        )}
                      </div>
                      <input ref={fileInputRefs[vk]} type="file" accept="image/*"
                        onChange={handleViewFile(vk)}
                        style={{ display: 'none' }}
                        data-testid={`color-view-${vk}-input`} />
                      {img ? (
                        <div style={{
                          width: '100%', height: 100,
                          background: 'repeating-conic-gradient(#f1f5f9 0% 25%, #fff 0% 50%) 50% / 12px 12px',
                          borderRadius: 4, position: 'relative', overflow: 'hidden',
                        }}>
                          <img src={img} alt={vk}
                            onClick={() => fileInputRefs[vk].current?.click()}
                            style={{ width: '100%', height: '100%', objectFit: 'contain', cursor: 'pointer' }} />
                        </div>
                      ) : (
                        <Button variant="outline" size="sm"
                          onClick={() => fileInputRefs[vk].current?.click()}
                          style={{ width: '100%', height: 100, display: 'flex', flexDirection: 'column', gap: 4 }}
                          data-testid={`color-view-${vk}-btn`}>
                          <Upload size={14} />
                          <span style={{ fontSize: 10 }}>Upload</span>
                        </Button>
                      )}
                      {img && (
                        <Button size="sm" variant="ghost" onClick={() => removeBackgroundFor(vk)}
                          disabled={removingBgKey === vk}
                          style={{
                            width: '100%', marginTop: 4, fontSize: 11,
                            color: '#7c3aed', justifyContent: 'center',
                          }}
                          data-testid={`remove-bg-${vk}-btn`}>
                          {removingBgKey === vk ? (
                            <><Loader2 size={11} className="animate-spin" style={{ marginRight: 4 }} />Removing…</>
                          ) : (
                            <><Scissors size={11} style={{ marginRight: 4 }} />Remove Background</>
                          )}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setColorTarget(null)} disabled={uploadingColor}>Cancel</Button>
            <Button onClick={saveColor} disabled={uploadingColor} style={{ background: '#dc2626', color: '#fff' }}
              data-testid="save-color-btn">
              {uploadingColor ? <><Loader2 className="animate-spin" size={14} style={{ marginRight: 6 }} />Saving…</> : <>Save Color</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InternalCatalog;
